/* global ElementUtil, Service */
import {ElementUtil} from '../../utils/elementUtil.js';
import {pageRegistry} from '../../utils/pageRegistry.js';
import {Service} from '../../service/service.js';

/**
 * Workspace Management dialog — save / load / remove named workspace save states.
 */
export var WorkspaceManagement = {

	open: function() {
		var currentName = pageRegistry.getWorkspaceName() || 'Default';

		var managementWin = ElementUtil.create({
			tag: 'dl-dialog',
			title: 'Workspace: ' + currentName,
			icon: 'vaadin:cog',
			width: 0.55,
			'mobile-width': 0.9,
			height: 0.55,
			scope: 'app',
			listeners: {
				ready: function() {
					managementWin.show();
					WorkspaceManagement.buildContent(managementWin);
				}
			}
		});
	},

	buildContent: function(managementWin) {
		var selectedWorkspace = null;
		var workspaceGrid = null;
		var loadButton = null;
		var removeButton = null;

		var updateTitle = function() {
			var name = pageRegistry.getWorkspaceName() || 'Default';
			if (managementWin.headerNode) {
				var titleSpan = managementWin.headerNode.querySelector('.title-text');
				if (titleSpan) {
					titleSpan.innerHTML = ' Workspace: ' + name;
				}
			}
			managementWin.title = 'Workspace: ' + name;
		};

		var updateSelectionButtons = function() {
			var hasSelection = !!selectedWorkspace;
			if (loadButton) {
				loadButton.disabled = !hasSelection;
			}
			if (removeButton) {
				removeButton.disabled = !hasSelection;
			}
		};

		var loadGrid = function() {
			var loadMask = workspaceGrid.showLoading ? workspaceGrid.showLoading() : null;
			Service.getWorkspaceService().listWorkspaces().then(function(workspaces) {
				if (loadMask) {
					loadMask.remove();
				}
				workspaceGrid.items = workspaces || [];
				workspaceGrid.activeItem = null;
				workspaceGrid.selectedItems = [];
				selectedWorkspace = null;
				updateSelectionButtons();
			}).catch(function() {
				if (loadMask) {
					loadMask.remove();
				}
				workspaceGrid.items = [];
				selectedWorkspace = null;
				updateSelectionButtons();
			});
		};

		var persistSnapshot = function(name, options) {
			options = options || {};
			var snapshot = pageRegistry.collectSaveState();
			var payload = {
				name: name,
				activePageKey: snapshot.activePageKey,
				pages: snapshot.pages
			};

			var request;
			if (options.workspaceId) {
				payload.workspaceId = options.workspaceId;
				request = Service.getWorkspaceService().updateWorkspace(options.workspaceId, payload);
			} else {
				request = Service.getWorkspaceService().saveWorkspace(payload);
			}

			return request.then(function(saved) {
				var savedId = saved && saved.workspaceId ? saved.workspaceId : options.workspaceId;
				pageRegistry.setWorkspaceIdentity(name, savedId);
				updateTitle();
				loadGrid();
				window.DL.MessageUtil.showToast('Success', 'Workspace saved', { theme: 'success' });
				return saved;
			}).catch(function() {
				window.DL.MessageUtil.showToastError('Unable to save workspace');
				return Promise.reject();
			});
		};

		var promptForSaveName = function(defaultName, onSave) {
			var promptWin = ElementUtil.create({
				tag: 'dl-dialog',
				title: 'Save Workspace',
				icon: 'vaadin:database',
				width: 420,
				height: 180,
				scope: 'app',
				listeners: {
					ready: function() {
						promptWin.show();

						var nameField = ElementUtil.create({
							tag: 'vaadin-text-field',
							label: 'Save name',
							required: true,
							style: 'width: 100%;',
							value: defaultName || ''
						});

						var saveButton = ElementUtil.create({
							tag: 'vaadin-button',
							theme: 'primary',
							innerHTML: 'Save',
							listeners: {
								click: function() {
									var name = (nameField.value || '').trim();
									if (!name) {
										nameField.validate();
										return;
									}
									onSave(name).then(function() {
										promptWin.close();
									}).catch(function() {
										// keep prompt open on failure
									});
								}
							}
						});

						var cancelButton = ElementUtil.create({
							tag: 'vaadin-button',
							innerHTML: 'Cancel',
							listeners: {
								click: function() {
									promptWin.close();
								}
							}
						});

						promptWin.addToContentElement(ElementUtil.create({
							tag: 'div',
							children: [
								nameField,
								{
									tag: 'div',
									style: 'margin-top: 16px; text-align: right;',
									children: [cancelButton, {tag: 'span', innerHTML: ' '}, saveButton]
								}
							]
						}));
						setTimeout(function() {
							nameField.focus();
						}, 50);
					}
				}
			});
		};

		var actionSave = function() {
			if (pageRegistry.hasNamedSaveState()) {
				window.DL.MessageUtil.showConfirm({
					title: 'Save Workspace',
					message: 'Workspace "' + pageRegistry.getWorkspaceName() + '" is currently loaded. Save overwrites it; Save As creates a new save state.',
					buttons: [
						{
							text: 'Save',
							theme: 'primary',
							handler: function() {
								var workspaceId = pageRegistry.getWorkspaceId();
								var name = pageRegistry.getWorkspaceName();
								if (workspaceId) {
									persistSnapshot(name, { workspaceId: workspaceId });
								} else {
									persistSnapshot(name);
								}
							}
						},
						{
							text: 'Save As',
							handler: function() {
								promptForSaveName('', function(name) {
									return persistSnapshot(name);
								});
							}
						},
						{
							text: 'Cancel',
							handler: function() {}
						}
					]
				});
			} else {
				promptForSaveName('', function(name) {
					return persistSnapshot(name);
				});
			}
		};

		var actionLoad = function() {
			if (!selectedWorkspace) {
				return;
			}

			window.DL.MessageUtil.showConfirm({
				title: 'Load Workspace',
				message: 'Replace the current workspace with "' + selectedWorkspace.name + '"?',
				buttons: [
					{
						text: 'Load',
						theme: 'primary',
						handler: function() {
							Service.getWorkspaceService().getWorkspace(selectedWorkspace.workspaceId).then(function(workspace) {
								var mainContent = document.querySelector('#mainContent');
								if (!mainContent) {
									return;
								}
								var restorePayload = {
									workspaceId: workspace.workspaceId,
									name: workspace.name,
									activePageKey: workspace.activePageKey,
									pages: (workspace.pages || []).map(function(page) {
										return {
											pageKey: page.pageKey,
											title: page.title || page.pageKey,
											url: page.url || WorkspaceManagement.resolveUrl(page.pageKey),
											filters: page.filters || {}
										};
									})
								};
								mainContent.stageForRestore(restorePayload);
								updateTitle();
								managementWin.close();
								window.DL.MessageUtil.showToast('Success', 'Workspace loaded', { theme: 'success' });
							}).catch(function() {
								window.DL.MessageUtil.showToastError('Unable to load workspace');
							});
						}
					},
					{
						text: 'Cancel',
						handler: function() {}
					}
				]
			});
		};

		var actionRemove = function() {
			if (!selectedWorkspace) {
				return;
			}

			window.DL.MessageUtil.showConfirm({
				title: 'Remove Save State',
				message: 'Remove saved workspace "' + selectedWorkspace.name + '"? Open pages stay as they are.',
				buttons: [
					{
						text: 'Remove',
						theme: 'error primary',
						handler: function() {
							Service.getWorkspaceService().deleteWorkspace(selectedWorkspace.workspaceId).then(function() {
								loadGrid();
								window.DL.MessageUtil.showToast('Success', 'Workspace save state removed', { theme: 'success' });
							}).catch(function() {
								window.DL.MessageUtil.showToastError('Unable to remove workspace');
							});
						}
					},
					{
						text: 'Cancel',
						handler: function() {}
					}
				]
			});
		};

		loadButton = ElementUtil.create({
			tag: 'vaadin-button',
			itemid: 'load',
			'class': 'spacer-toolbar-item',
			disabled: true,
			children: [ElementUtil.makeIcon('vaadin:outbox', ' Load')],
			listeners: {
				click: actionLoad
			}
		});

		removeButton = ElementUtil.create({
			tag: 'vaadin-button',
			itemid: 'remove',
			theme: 'error primary',
			'class': 'spacer-toolbar-item',
			disabled: true,
			style: 'margin-left: auto;',
			children: [ElementUtil.makeIcon('vaadin:trash', ' Remove')],
			listeners: {
				click: actionRemove
			}
		});

		var toolbar = ElementUtil.create({
			tag: 'dl-toolbar',
			slot: 'toolbar',
			theme: 'spacing',
			bodyStyle: 'display: flex; align-items: center; width: 100%;',
			children: [
				{
					tag: 'vaadin-button',
					itemid: 'save',
					theme: 'primary',
					'class': 'spacer-toolbar-item',
					children: [ElementUtil.makeIcon('vaadin:database', ' Save')],
					listeners: {
						click: actionSave
					}
				},
				loadButton,
				removeButton
			]
		});

		workspaceGrid = ElementUtil.create({
			tag: 'vaadin-grid',
			slot: 'primary',
			theme: 'row-stripes column-borders',
			style: 'height: 100%;',
			defaults: {
				tag: 'vaadin-grid-sort-column',
				resizable: true
			},
			children: [
				{
					path: 'name',
					header: 'Name',
					flexGrow: 1
				},
				{
					path: 'updateDts',
					header: 'Updated',
					width: '12em',
					renderer: function(root, column, rowData) {
						root.innerHTML = '';
						var value = rowData.item.updateDts;
						if (value) {
							root.textContent = new Date(value).toLocaleString();
						}
					}
				},
				{
					path: 'activePageKey',
					header: 'Active Page',
					width: '12em'
				}
			],
			listeners: {
				'active-item-changed': function(event) {
					selectedWorkspace = event.detail.value;
					workspaceGrid.selectedItems = selectedWorkspace ? [selectedWorkspace] : [];
					updateSelectionButtons();
				}
			}
		});

		var layout = ElementUtil.create({
			tag: 'dl-flex-layout',
			style: 'height: 100%;',
			children: [
				toolbar,
				workspaceGrid
			]
		});

		managementWin.addToContentElement(layout);
		loadGrid();
		updateTitle();

		window.addEventListener('workspacenamechange', updateTitle);
	},

	resolveUrl: function(pageKey) {
		var sideNav = document.querySelector('#mainSideNav');
		if (sideNav && sideNav.allMenuItems) {
			var found = sideNav.allMenuItems.find(function(item) {
				return item.itemDetails && item.itemDetails.key === pageKey;
			});
			if (found) {
				return found.itemDetails.url;
			}
		}
		return './views/dashboard.html';
	}
};
